import {
  Loader2,
  Share2,
  X,
} from 'lucide-react'
import {
  useCallback,
  useEffect,
  useState,
} from 'react'
import {
  customIntegrationsService,
  getUserFacingApiErrorMessage,
  sharedIntegrationsService,
} from '../services/api'
import type {
  SharedIntegrationSummary,
} from '../services/api'
import {
  OnboardingEvents,
  trackEvent,
} from '../analytics/posthog'

function authKindLabel(authKind: string): string {
  switch (authKind) {
    case 'oauth_ac': return 'OAuth'
    case 'api_key': return 'API key'
    case 'basic_auth': return 'Basic auth'
    default: return authKind
  }
}

function initialFor(name: string): string {
  return name.trim().charAt(0).toUpperCase() || '?'
}

function SharedIntegrationTile({
  integration,
  onClick,
}: {
  integration: SharedIntegrationSummary
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex flex-col items-center justify-center gap-2 p-4 rounded-xl transition-colors relative group ${
        integration.connected
          ? 'bg-green-50 border-2 border-green-200 hover:bg-green-100'
          : 'bg-white border border-gray-200 hover:border-primary-300 hover:shadow-sm'
      }`}
    >
      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-gradient-to-br from-primary-400 to-primary-600 text-white text-sm font-semibold">
        {initialFor(integration.display_name)}
      </div>
      <span className="text-xs font-medium text-gray-700 truncate max-w-full">{integration.display_name}</span>
      {integration.connected && (
        <div className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center shadow-sm">
          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <polyline points="20 6 9 17 4 12" strokeWidth="3" />
          </svg>
        </div>
      )}
    </button>
  )
}

function SharedIntegrationDetailModal({
  integration,
  onClose,
}: {
  integration: SharedIntegrationSummary
  onClose: () => void
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleConnect = async () => {
    trackEvent(OnboardingEvents.SHARED_INTEGRATION_CONNECT_CLICKED, {
      auth_kind: integration.auth_kind,
      connected: integration.connected,
    })
    setBusy(true)
    setError(null)
    try {
      const url = integration.auth_kind === 'oauth_ac'
        ? customIntegrationsService.oauthUrl(integration.id)
        : await customIntegrationsService.createSecretRequest(integration.id)
      // busy stays set on success — the modal unmounts when the navigation starts.
      window.location.href = url
    } catch (err) {
      setError(getUserFacingApiErrorMessage(err, "Couldn't start connecting just now. Give it another try."))
      setBusy(false)
    }
  }

  const handleClose = () => {
    trackEvent(OnboardingEvents.SHARED_INTEGRATION_MODAL_CLOSED)
    onClose()
  }

  const isOAuth = integration.auth_kind === 'oauth_ac'
  let connectLabel: string
  if (isOAuth) {
    connectLabel = integration.connected ? 'Reconnect via OAuth' : 'Connect via OAuth'
  } else {
    connectLabel = integration.connected ? 'Re-enter credentials' : 'Enter credentials'
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-lg w-full">
        <div className="p-6 border-b border-gray-200 flex items-start justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-gradient-to-br from-primary-400 to-primary-600 text-white text-base font-semibold">
              {initialFor(integration.display_name)}
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-bold text-gray-900 truncate">{integration.display_name}</h2>
              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                <span className="text-xs text-gray-500">{authKindLabel(integration.auth_kind)}</span>
                {integration.connected && (
                  <span className="px-1.5 py-0.5 text-[10px] font-medium bg-green-100 text-green-700 rounded">
                    Connected
                  </span>
                )}
              </div>
            </div>
          </div>
          <button onClick={handleClose} className="p-1 text-gray-400 hover:text-gray-600 rounded-lg flex-shrink-0">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1">About</p>
            <p className="text-sm text-gray-700">{integration.description}</p>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1">Internal name</p>
            <code className="text-xs bg-gray-50 px-2 py-1 rounded font-mono text-gray-700">
              {integration.name}
            </code>
          </div>
          <p className="text-xs text-gray-500">
            This integration was shared with you. Connect your own account to let your assistant use it — your credentials stay private to you.
          </p>
          {error && (
            <p className="text-sm text-red-600">{error}</p>
          )}
        </div>

        <div className="p-6 border-t border-gray-200 flex items-center justify-end">
          <button
            onClick={() => void handleConnect()}
            disabled={busy}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {connectLabel}
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 * Recipient-facing list of custom integrations an admin shared with the current
 * user. Ungated (a recipient need not have the custom-integration-builder flag),
 * and self-hiding: renders nothing until it has loaded at least one share, so it
 * never shows an empty card to the vast majority of users who have none.
 */
export function SharedIntegrationsCard() {
  const [integrations, setIntegrations] = useState<SharedIntegrationSummary[]>([])
  const [loaded, setLoaded] = useState(false)
  const [selected, setSelected] = useState<SharedIntegrationSummary | null>(null)

  const load = useCallback(async () => {
    try {
      const data = await sharedIntegrationsService.list()
      setIntegrations(data)
    } catch (error) {
      console.error('Failed to load shared integrations:', error)
    } finally {
      setLoaded(true)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  // Nothing shared with this user → render nothing at all (no empty card).
  if (!loaded || integrations.length === 0) return null

  return (
    <>
      <div className="bg-white rounded-2xl border border-gray-200 p-6 hover:shadow-md transition-shadow">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-primary-50 flex items-center justify-center">
            <Share2 className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Shared with you</h3>
            <p className="text-xs text-gray-500">Integrations shared with you — connect your own account</p>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3">
          {integrations.map((integration) => (
            <SharedIntegrationTile
              key={integration.id}
              integration={integration}
              onClick={() => {
                trackEvent(OnboardingEvents.SHARED_INTEGRATION_OPENED, { connected: integration.connected })
                setSelected(integration)
              }}
            />
          ))}
        </div>
      </div>

      {selected && (
        <SharedIntegrationDetailModal
          integration={selected}
          onClose={() => setSelected(null)}
        />
      )}
    </>
  )
}
